"use client";

import { Brain, Heart, Waves, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheMedacubeAdvantage() {
  const intro = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const quote = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const features = [{
    number: "01",
    title: "Automated Dispensing",
    description: "The MedaCube system precisely organizes and dispenses medication at the exact scheduled time. By removing the manual burden of pill sorting, we eliminate human error and ensure your loved ones receive the correct dosage every single time.",
    icon: Brain,
    offset: "lg:mt-0"
  }, {
    number: "02",
    title: "Caregiver Oversight",
    description: "Stay connected to your loved one's health journey from anywhere. Our platform provides real-time alerts for missed doses, allowing caregivers to intervene immediately and maintain consistent medication adherence without constant worry.",
    icon: Heart,
    offset: "lg:mt-20"
  }, {
    number: "03",
    title: "Simplified refills and support",
    description: "Stability is the foundation of wellness. By automating the daily routine, we reduce the stress of medication management, allowing families to focus on quality time rather than the logistics of pill bottles and schedules.",
    icon: Waves,
    offset: "lg:mt-0"
  }];
  return <section data-section-id="938" id="the-medacube-advantage" className="bg-background text-foreground relative overflow-hidden py-24 md:py-32 lg:py-40">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-40" style={{
      background: "radial-gradient(circle at 50% 30%, hsla(83, 23%, 53%, 0.06) 0%, transparent 60%)"
    }} />

      <div className="relative container mx-auto px-6">
        {}
        <div ref={intro.ref} className={`mx-auto max-w-[720px] text-center transition-all duration-1000 ease-out ${intro.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-default text-xs uppercase tracking-[0.3em] text-primary">
            The AthenaCare Edge
          </p>
          <h2 className="font-default font-medium text-foreground mt-8 leading-[1.05]" style={{
          fontSize: "clamp(2rem, 5vw, 5rem)"
        }}>
            The precision
            <br />
            of <em className="italic font-normal">care</em>
          </h2>
          <p className="font-default text-foreground/70 mt-10 text-[20px] leading-relaxed">
            When technology meets compassionate oversight, medication management becomes effortless. 
            AthenaCare integrates the MedaCube system to provide families with the peace of mind 
            that comes from knowing every dose is accounted for, every day.
          </p>
        </div>

        {}
        <div className="mt-[100px] grid grid-cols-1 gap-16 md:gap-20 lg:grid-cols-3 lg:gap-12">
          {features.map((feature: any, i) => {
          const Icon = feature.icon;
          const card = useRevealOnScroll<HTMLDivElement>({
            threshold: 0.15
          });
          return <div data-index={i} ref={card.ref} key={feature.number} className={`flex flex-col p-6 md:p-8 ${feature.offset} transition-all duration-1000 ease-out ${card.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
            transitionDelay: `${i * 150}ms`
          }}>
                <Icon className="text-primary" strokeWidth={1} size={60} aria-hidden="true" />
                
                <h3 className="font-default font-medium text-foreground mt-4 text-2xl md:text-[28px] leading-tight">
                  {feature.title}
                </h3>
                <p className="font-default text-foreground/70 mt-5 text-[15px] leading-relaxed">
                  {feature.description}
                </p>
                <Separator className="bg-foreground/15 mt-10" />
                {feature.cta && <Link to={feature.cta.to} className="font-default border border-foreground/10 mt-8 inline-flex items-center justify-center px-6 py-3 text-[10px] uppercase tracking-[0.3em] transition-all duration-300 text-primary-foreground !font-semibold hover:text-primary hover:bg-primary/5 hover:border-primary bg-primary">
                    {feature.cta.text}
                  </Link>}
              </div>;
        })}

          {}
          <div data-index={3} className="lg:col-span-3 mt-12 lg:mt-24">
            <FourthFeature />
          </div>
        </div>

        {}
        <div ref={quote.ref} className={`mx-auto mt-32 max-w-[1000px] text-center md:mt-40 transition-all duration-1000 ease-out ${quote.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <blockquote className="font-default italic font-light text-foreground leading-[1.15]" style={{
          fontSize: "clamp(2rem, 4vw, 3rem)"
        }}>
            Reliability is the greatest
            <br />
            form of love you can
            <br />
            provide for your family.
          </blockquote>
          
        </div>
      </div>
    </section>;
}
function FourthFeature() {
  const card = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={card.ref} className={`mx-auto grid max-w-5xl grid-cols-1 items-start gap-10 md:grid-cols-12 md:gap-14 transition-all duration-1000 ease-out ${card.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      <div className="md:col-span-4 flex justify-start md:justify-center">
        <Sparkles className="text-primary" strokeWidth={1} size={60} aria-hidden="true" />
      </div>
      <div className="md:col-span-8 flex flex-col">
        
        <h3 className="font-default font-medium text-foreground mt-4 text-[28px] leading-tight md:text-[32px]">
          Proactive Health Monitoring
        </h3>
        <p className="font-default text-foreground/70 mt-5 text-[15px] leading-relaxed max-w-2xl">
          Beyond simple dispensing, our system tracks adherence patterns to provide actionable 
          insights into your loved one's health. By identifying trends early, we empower 
          caregivers and medical professionals to make informed decisions that support 
          long-term independence and well-being.
        </p>
        <Separator className="bg-foreground/15 mt-10" />
        <Link to="/Contact" className="font-default border border-foreground/10 mt-8 inline-flex items-center justify-center px-6 py-3 text-[10px] uppercase tracking-[0.3em] transition-all duration-300 bg-primary !text-[rgba(255,255,255,1)] !font-bold hover:text-primary hover:bg-primary/5 hover:border-primary">
          Contact Us
        </Link>
      </div>
    </div>;
}